import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, Brain, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import LaTeXRenderer from '../../components/LaTeXRenderer';
import { ProctoringSettingsSelector } from '../../components/Proctoring';

function CreateTest() {
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [bankQuestionCounts, setBankQuestionCounts] = useState({});
  const [difficulty, setDifficulty] = useState('all');
  const [selectionMode, setSelectionMode] = useState('random'); // 'random' or 'manual'
  const [proctoringLevel, setProctoringLevel] = useState('none');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    config: {
      duration: 60,
      negative_marking: 0,
      attempts_allowed: 1,
      shuffle_questions: false,
      shuffle_choices: false,
      show_results_immediately: true,
      pass_percentage: 40,
      proctoring_enabled: true,
      tab_switch_limit: 3,
      require_fullscreen: true,
    },
    question_ids: [],
    has_psychometric_test: false,
    psychometric_results_visible: false,
    requires_sequential_completion: false,
    proctoring_level: 'none',
  });

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const response = await api.get('/question-banks');
      setBanks(response.data);
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const fetchQuestionsFromBanks = async (bankIds) => {
    try {
      const allQuestionsPromises = bankIds.map(bankId => 
        api.get(`/questions/bank/${bankId}`)
      );
      const responses = await Promise.all(allQuestionsPromises);
      const questions = responses.flatMap(r => r.data);
      setAllQuestions(questions);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to fetch questions');
    }
  };

  const handleBankToggle = (bankId) => {
    const newSelection = selectedBanks.includes(bankId)
      ? selectedBanks.filter(id => id !== bankId)
      : [...selectedBanks, bankId];
    setSelectedBanks(newSelection);
    
    // Set default count for newly selected bank
    if (!selectedBanks.includes(bankId)) {
      const bank = banks.find(b => b.id === bankId);
      setBankQuestionCounts(prev => ({ ...prev, [bankId]: Math.min(10, bank?.question_count || 10) }));
    } else {
      setBankQuestionCounts(prev => {
        const copy = { ...prev };
        delete copy[bankId];
        return copy;
      });
    }
    
    if (newSelection.length > 0) {
      fetchQuestionsFromBanks(newSelection);
    } else {
      setAllQuestions([]);
      setFormData({ ...formData, question_ids: [] });
    }
  };

  const handleBankCountChange = (bankId, count) => {
    const bank = banks.find(b => b.id === bankId);
    const maxQ = bank?.question_count || 0;
    setBankQuestionCounts(prev => ({ ...prev, [bankId]: Math.max(1, Math.min(count, maxQ)) }));
  };

  const totalSelectedCount = Object.entries(bankQuestionCounts)
    .filter(([id]) => selectedBanks.includes(id))
    .reduce((sum, [, count]) => sum + count, 0);

  const handleRandomSelection = () => {
    if (allQuestions.length === 0) {
      toast.error('No questions available. Please select question banks first.');
      return;
    }

    let selectedIds = [];

    // Pick specified number of questions from each bank
    for (const bankId of selectedBanks) {
      const count = bankQuestionCounts[bankId] || 0;
      if (count <= 0) continue;

      let bankQuestions = allQuestions.filter(q => q.bank_id === bankId);
      
      // Filter by difficulty if not 'all'
      if (difficulty !== 'all') {
        bankQuestions = bankQuestions.filter(q => q.difficulty === difficulty);
      }

      // Randomly shuffle and pick
      const shuffled = [...bankQuestions].sort(() => Math.random() - 0.5);
      const picked = shuffled.slice(0, Math.min(count, shuffled.length));
      selectedIds.push(...picked.map(q => q.id));
    }

    if (selectedIds.length === 0) {
      toast.error('No questions match the selected criteria');
      return;
    }
    
    setFormData({ ...formData, question_ids: selectedIds });
    toast.success(`${selectedIds.length} questions randomly selected from ${selectedBanks.length} bank(s)`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.question_ids.length === 0) {
      toast.error('Please select at least one question');
      return;
    }
    try {
      await api.post('/tests', formData);
      toast.success('Test created successfully');
      navigate('/admin/tests');
    } catch (error) {
      console.error('Error creating test:', error);
      toast.error('Failed to create test');
    }
  };

  const toggleQuestion = (questionId) => {
    const ids = formData.question_ids.includes(questionId)
      ? formData.question_ids.filter(id => id !== questionId)
      : [...formData.question_ids, questionId];
    setFormData({ ...formData, question_ids: ids });
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/tests')}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tests
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 mb-8" data-testid="create-test-title">Create Test</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Test Title *</Label>
                <Input
                  id="title"
                  data-testid="test-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="test-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Test Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time">Start Time *</Label>
                <Input
                  id="start_time"
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_time">End Time *</Label>
                <Input
                  id="end_time"
                  type="datetime-local"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes) *</Label>
                <Input
                  id="duration"
                  data-testid="duration-input"
                  type="number"
                  min="1"
                  value={formData.config.duration}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, duration: parseInt(e.target.value) }
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="attempts">Attempts Allowed *</Label>
                <Input
                  id="attempts"
                  data-testid="attempts-input"
                  type="number"
                  min="1"
                  value={formData.config.attempts_allowed}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, attempts_allowed: parseInt(e.target.value) }
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="negative_marking">Negative Marking</Label>
                <Input
                  id="negative_marking"
                  data-testid="negative-marking-input"
                  type="number"
                  step="0.25"
                  min="0"
                  value={formData.config.negative_marking}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, negative_marking: parseFloat(e.target.value) }
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pass_percentage">Pass Percentage</Label>
                <Input
                  id="pass_percentage"
                  data-testid="pass-percentage-input"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.config.pass_percentage}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, pass_percentage: parseFloat(e.target.value) }
                  })}
                />
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="shuffle_questions"
                  data-testid="shuffle-questions-checkbox"
                  checked={formData.config.shuffle_questions}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    config: { ...formData.config, shuffle_questions: checked }
                  })}
                />
                <Label htmlFor="shuffle_questions" className="cursor-pointer">Shuffle Questions</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="shuffle_choices"
                  data-testid="shuffle-choices-checkbox"
                  checked={formData.config.shuffle_choices}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    config: { ...formData.config, shuffle_choices: checked }
                  })}
                />
                <Label htmlFor="shuffle_choices" className="cursor-pointer">Shuffle Choices</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="show_results_immediately"
                  data-testid="show-results-checkbox"
                  checked={formData.config.show_results_immediately}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    config: { ...formData.config, show_results_immediately: checked }
                  })}
                />
                <Label htmlFor="show_results_immediately" className="cursor-pointer font-medium">Show Results to Users After Submission</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="proctoring_enabled"
                  data-testid="proctoring-enabled-checkbox"
                  checked={formData.config.proctoring_enabled}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    config: { ...formData.config, proctoring_enabled: checked }
                  })}
                />
                <Label htmlFor="proctoring_enabled" className="cursor-pointer">Enable Proctoring</Label>
              </div>
            </div>
          </Card>

          {/* Psychometric Test Section */}
          <Card className="p-6 border-2 border-purple-200 bg-purple-50/30">
            <div className="flex items-center gap-3 mb-4">
              <Brain className="w-6 h-6 text-purple-600" />
              <h3 className="text-lg font-semibold text-purple-900">Psychometric Assessment</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-purple-200">
                <div>
                  <Label htmlFor="psychometric_toggle" className="font-medium text-gray-900">Include Psychometric Test</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Add a 60-question personality and behavioral assessment (60 minutes)
                  </p>
                </div>
                <Switch
                  id="psychometric_toggle"
                  data-testid="psychometric-toggle"
                  checked={formData.has_psychometric_test}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    has_psychometric_test: checked
                  })}
                />
              </div>
              
              {formData.has_psychometric_test && (
                <div className="p-4 bg-white rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="psychometric_visible" className="font-medium text-gray-900">Show Results to Students</Label>
                      <p className="text-sm text-gray-500 mt-1">
                        Allow students to view their psychometric results after completion
                      </p>
                    </div>
                    <Switch
                      id="psychometric_visible"
                      data-testid="psychometric-visible-toggle"
                      checked={formData.psychometric_results_visible}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        psychometric_results_visible: checked
                      })}
                    />
                  </div>
                  
                  <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                    <h4 className="font-medium text-purple-800 mb-2">Assessment Includes:</h4>
                    <ul className="text-sm text-purple-700 space-y-1">
                      <li>• 60 questions measuring 10 personality traits</li>
                      <li>• Likert scale responses (1-5: Strongly Disagree to Strongly Agree)</li>
                      <li>• 60 minutes duration</li>
                      <li>• Automated scoring with detailed trait analysis</li>
                      <li>• Traits: Openness, Conscientiousness, Extraversion, Agreeableness, Emotional Stability, Leadership, Teamwork, Integrity, Stress Tolerance, Decision Making</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Sequential Completion Toggle */}
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <Label htmlFor="sequential_toggle" className="font-medium text-gray-900">Require Sequential Completion</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Students must complete all previous assigned tests before accessing this one
                  </p>
                </div>
                <Switch
                  id="sequential_toggle"
                  data-testid="sequential-completion-toggle"
                  checked={formData.requires_sequential_completion}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    requires_sequential_completion: checked
                  })}
                />
              </div>

              {/* AI Proctoring Settings */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">AI Proctoring</h4>
                </div>
                <ProctoringSettingsSelector
                  value={proctoringLevel}
                  onChange={(level) => {
                    setProctoringLevel(level);
                    setFormData(prev => ({
                      ...prev,
                      proctoring_level: level
                    }));
                  }}
                />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Select Questions</h3>
            
            {/* Selection Mode Toggle */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant={selectionMode === 'random' ? 'default' : 'outline'}
                  onClick={() => setSelectionMode('random')}
                  className={selectionMode === 'random' ? 'bg-blue-600' : ''}
                >
                  🎲 Random Selection
                </Button>
                <Button
                  type="button"
                  variant={selectionMode === 'manual' ? 'default' : 'outline'}
                  onClick={() => setSelectionMode('manual')}
                  className={selectionMode === 'manual' ? 'bg-blue-600' : ''}
                >
                  ✋ Manual Selection
                </Button>
              </div>
            </div>

            {selectionMode === 'random' ? (
              <div className="space-y-4">
                {/* Step 1: Select Question Banks with per-bank count */}
                <div className="space-y-2">
                  <Label>Step 1: Select Question Banks & Number of Questions *</Label>
                  <div className="grid grid-cols-1 gap-3 p-4 border rounded-lg bg-gray-50 max-h-80 overflow-y-auto">
                    {banks.map((bank) => {
                      const isSelected = selectedBanks.includes(bank.id);
                      return (
                        <div key={bank.id} className={`flex items-center gap-3 p-3 rounded transition-colors ${isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-white hover:bg-gray-50 border border-transparent'}`}>
                          <Checkbox
                            id={`bank-${bank.id}`}
                            checked={isSelected}
                            onCheckedChange={() => handleBankToggle(bank.id)}
                            data-testid={`bank-checkbox-${bank.id}`}
                          />
                          <Label htmlFor={`bank-${bank.id}`} className="flex-1 cursor-pointer">
                            <p className="font-medium text-sm">{bank.title}</p>
                            <p className="text-xs text-gray-500">{bank.question_count} questions available • {bank.subject}</p>
                          </Label>
                          {isSelected && (
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-xs text-gray-500">Pick</span>
                              <Input
                                type="number"
                                min="1"
                                max={bank.question_count}
                                value={bankQuestionCounts[bank.id] || ''}
                                onChange={(e) => handleBankCountChange(bank.id, parseInt(e.target.value) || 1)}
                                className="w-20 h-8 text-sm text-center"
                                data-testid={`bank-count-${bank.id}`}
                              />
                              <span className="text-xs text-gray-400">/ {bank.question_count}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-sm text-gray-600">
                    {selectedBanks.length} bank(s) selected • <span className="font-semibold">{totalSelectedCount}</span> questions to pick • {allQuestions.length} available
                  </p>
                </div>

                {/* Step 2: Difficulty Filter (Optional) */}
                <div className="space-y-2">
                  <Label>Step 2: Filter by Difficulty (Optional)</Label>
                  <Select value={difficulty} onValueChange={setDifficulty}>
                    <SelectTrigger className="max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Difficulties</SelectItem>
                      <SelectItem value="easy">Easy Only</SelectItem>
                      <SelectItem value="medium">Medium Only</SelectItem>
                      <SelectItem value="hard">Hard Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Random Selection Button */}
                <div className="pt-4">
                  <Button
                    type="button"
                    onClick={handleRandomSelection}
                    disabled={selectedBanks.length === 0 || totalSelectedCount === 0}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="generate-random-btn"
                  >
                    Generate {totalSelectedCount} Random Questions
                  </Button>
                  <p className="text-sm text-gray-600 mt-2">
                    {formData.question_ids.length > 0 && (
                      <span className="text-green-600 font-medium">
                        ✓ {formData.question_ids.length} questions selected
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Question Bank</Label>
                  <Select onValueChange={(value) => {
                    setSelectedBanks([value]);
                    fetchQuestionsFromBanks([value]);
                  }}>
                    <SelectTrigger data-testid="question-bank-select">
                      <SelectValue placeholder="Choose a question bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((bank) => (
                        <SelectItem key={bank.id} value={bank.id}>
                          {bank.title} ({bank.question_count} questions)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {allQuestions.length > 0 && (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    <p className="text-sm text-gray-600 mb-2">
                      Selected: {formData.question_ids.length} questions
                    </p>
                    {allQuestions.map((q, index) => (
                      <div key={q.id} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50" data-testid={`question-item-${index}`}>
                        <Checkbox
                          id={q.id}
                          checked={formData.question_ids.includes(q.id)}
                          onCheckedChange={() => toggleQuestion(q.id)}
                        />
                        <Label htmlFor={q.id} className="flex-1 cursor-pointer">
                          <div className="font-medium">
                            <LaTeXRenderer text={q.question_text} />
                          </div>
                          <div className="flex gap-2 mt-1">
                            <span className="text-xs px-2 py-1 bg-gray-100 rounded">{q.difficulty}</span>
                            <span className="text-xs px-2 py-1 bg-blue-100 rounded">{q.points} pts</span>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/admin/tests')}>
              Cancel
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-test-button">
              Create Test
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export default CreateTest;